import React, { useState, useEffect, useRef } from 'react';
import { AutocompleteProp } from './types';
import { getLocationDataFromAddress } from '../../../helpers/geolocation';

const Autocomplete: React.FC<AutocompleteProp> = ({
  label,
  name,
  placeholder,
  required,
  type,
  formDataValue,
  setFormData,
  handleValidation,
  onBlur
}) => {
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const autocompleteInput = useRef(null);
  const [localValue, setLocalValue] = useState('');
  const options = {
    componentRestrictions: { country: ['us', 'ca'] },
    fields: ['formatted_address', 'address_components', 'geometry'],
    types: ['postal_code', 'route']
  };

  const initGoogleMaps = async () => {
    if (formDataValue) {
      const {
        zip,
        country,
        latitude,
        longitude,
        city,
        state,
        street,
        streetNumber,
        secondStreet
      } = await getLocationDataFromAddress(formDataValue);

      if (
        zip &&
        latitude &&
        longitude &&
        street &&
        streetNumber &&
        (country || city || state || secondStreet)
      ) {
        handleValidation('true', name, 'autocomplete');
      } else {
        handleValidation('', name, 'autocomplete');
        onBlur();
      }
    }

    if (autocompleteInput.current) {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        autocompleteInput.current as HTMLInputElement,
        options
      );
    }

    if (autocompleteRef.current) {
      autocompleteRef.current.addListener(
        'place_changed',
        async function updateAutocomplete() {
          const place = await (
            autocompleteRef.current as google.maps.places.Autocomplete
          ).getPlace();

          if (place.formatted_address) {
            setLocalValue(place.formatted_address);
          }

          const _place = place.address_components as {
            types: string[];
            long_name: string;
            short_name: string;
            geometry: object;
          }[];

          const _location = place.geometry?.location as {
            lat: () => number;
            lng: () => number;
          };

          const zipCode = _place.find(item =>
            item.types.includes('postal_code')
          );
          const latitude = _location.lat();
          const longitude = _location.lng();
          const street = _place.find(item => item.types.includes('route'));
          const streetNumber = _place.find(item =>
            item.types.includes('street_number')
          );
          const secondStreet = _place.find(item =>
            item.types.includes('subpremise')
          );
          const city = _place.find(item => item.types.includes('locality'));
          const state = _place.find(item =>
            item.types.includes('administrative_area_level_1')
          );
          const country = _place.find(item => item.types.includes('country'));

          setFormData(prevFormData => ({
            ...prevFormData,
            [name]: place.formatted_address ? place.formatted_address : '',
            ['zip']: zipCode ? zipCode.long_name : '',
            ['latitude']: latitude ? latitude : '',
            ['longitude']: longitude ? longitude : '',
            ['street']: `${streetNumber?.short_name || ''} ${
              street?.short_name || ''
            }`,
            ['secondStreet']: secondStreet ? secondStreet.short_name : '',
            ['city']: city ? city.long_name : '',
            ['state']: state ? state.short_name : '',
            ['country']: country ? country.short_name : ''
          }));

          if (
            streetNumber &&
            street?.short_name &&
            city &&
            state &&
            zipCode?.long_name
          ) {
            handleValidation('true', name, 'autocomplete');
          } else {
            handleValidation('', name, 'autocomplete');
          }
        }
      );
    }
  };

  useEffect(() => {
    if (formDataValue) {
      setLocalValue(formDataValue);
    }

    if (window.google?.maps) {
      initGoogleMaps();
    } else {
      const maxAttempts = 20;
      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        if (window.google?.maps) {
          clearInterval(interval);
          initGoogleMaps();
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
        }
      }, 500);
    }
  }, []);

  return (
    <input
      className="field__input"
      type={type}
      ref={autocompleteInput}
      id={label.trim().replace(' ', '_').toLowerCase()}
      name={name}
      value={localValue}
      placeholder={placeholder}
      required={required === 'true' ? true : false}
      onInput={e => setLocalValue(e.currentTarget.value)}
      onChange={() => {
        handleValidation('', name, 'autocomplete');
      }}
      onBlur={onBlur}
    />
  );
};

export default Autocomplete;
